import { useTranslations } from "@zapier/i18n";

import {
  Button,
  Key,
  ModalDialogBody,
  ModalDialogFooter,
  ModalDialogHeader,
  ModalDialogRoot,
  Text,
} from "@zapier/design-system";

import {
  FolderShare,
  FolderTreeItem,
  useConvertToSharedFolder,
  useCreateFolderShares,
  useDeleteFolderShares,
  useFolderSharing,
} from "@zapier/client";
import { SharingTypeahead } from "@zapier/shared";
import { FolderSharingModalList } from "./FolderSharingModalList";
import styles from "./FolderSharingModal.module.css";
import { usePendingSharesState } from "./usePendingSharesState/usePendingSharesState";
import { useNotification } from "@zapier/utils";

interface Props {
  folderId?: string;
  folder?: FolderTreeItem;
  isOpen: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

interface ContentProps extends Props {
  onSubmit: (
    currentFolder: FolderTreeItem | undefined,
    membersToAdd: FolderShare[],
    membersToDelete: FolderShare[],
  ) => void;
}

/** The modal content needs to be in another component that is conditionally rendered to keep
 * the useFolder hook from firing for every table row.
 */

function FolderShareModalContent({
  folderId,
  folder,
  setIsOpen,
  onSubmit,
}: ContentProps) {
  const t = useTranslations("non-mpe-folder-sharing-modal");

  const { shares, isHydrating, isSharesLoading } = useFolderSharing({
    folderId,
    rootId: folder?.rootId,
  });

  const {
    onAdd: onAddMember,
    onRemove: onRemoveMember,
    toAdd: membersToAdd,
    toDelete: membersToDelete,
    shares: members,
  } = usePendingSharesState(shares);

  const isSharedWithEveryone = members.some(
    ({ shareType }) => shareType === "account",
  );

  const selectMember = (id: Key | null) => {
    onAddMember(id);
  };

  const removeMember = (id: Key) => {
    onRemoveMember(id);
  };

  const submit = () => {
    onSubmit(folder, membersToAdd, membersToDelete);
    setIsOpen(false);
  };

  return (
    // eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- the form deliberately stops click/keydown propagation to keep the modal open and submit on Enter
    <form
      onSubmit={submit}
      onClick={(event) => event.stopPropagation()} // prevent the modal from closing when clicking inside the form
      onKeyDown={
        // if the enter key is being pressed submit the form
        (event) => {
          return event.key === "Enter" && submit();
        }
      }
    >
      <div className={styles.modalContent}>
        <ModalDialogHeader textAlign="start">
          {t("header.share-folder", {
            folderName: folder?.name || "",
            isFolderNameAvailable: folder?.name ? "true" : "false",
          })}
        </ModalDialogHeader>

        <ModalDialogBody textAlign="start">
          {folder?.isShared ? (
            <>
              {!!folder.children?.length && (
                <Text tag="p">{t("messages.subfolder-warning")}</Text>
              )}
              <Text tag="p">{t("messages.admin-access-info")}</Text>

              <div className={styles.typeahead}>
                <SharingTypeahead
                  showEveryoneOption={!isSharedWithEveryone}
                  onSelect={selectMember}
                />
              </div>

              <div>
                <FolderSharingModalList
                  members={members}
                  isLoading={isHydrating || isSharesLoading}
                  onRemove={removeMember}
                />
              </div>
            </>
          ) : (
            <Text tag="p">{t("messages.convert-to-shared-info")}</Text>
          )}
        </ModalDialogBody>

        <ModalDialogFooter>
          <Button variant="secondary" onPress={() => setIsOpen(false)}>
            {t("buttons.cancel")}
          </Button>
          <Button type="submit" onPress={submit}>
            {t("buttons.share")}
          </Button>
        </ModalDialogFooter>
      </div>
    </form>
  );
}

export function NonMpeFolderSharingModal(props: Props) {
  const { isOpen, setIsOpen, folderId } = props;
  const t = useTranslations("non-mpe-folder-sharing-modal");
  const { createNotification } = useNotification();
  const { createFolderShares } = useCreateFolderShares();
  const { deleteFolderShares } = useDeleteFolderShares();
  const { convertToSharedFolder } = useConvertToSharedFolder();

  const createSuccessToast = (folderName: string) => {
    const message = folderName
      ? t("notifications.sharing-updated", { folderName })
      : t("notifications.sharing-updated-generic");
    createNotification({
      message,
      type: "success",
    });
  };

  const createErrorToast = (folderName: string) => {
    const message = folderName
      ? t("notifications.unable-to-share", { folderName })
      : t("notifications.unable-to-share-generic");
    createNotification({
      message,
      type: "error",
    });
  };

  const submitConvertToSharedFolder = (
    folderId: string,
    folderName: string,
  ) => {
    convertToSharedFolder(
      { folderId },
      {
        onSuccess: () => {
          createSuccessToast(folderName);
        },
        onError: () => {
          createErrorToast(folderName);
        },
      },
    );
  };

  const updateFolderShares = (
    membersToAdd: FolderShare[],
    membersToDelete: FolderShare[],
    folderName: string,
  ) => {
    // Wrap this in a promise so that we can only resolve once
    // both the adds and deletes are done
    new Promise<void>((resolve, reject) => {
      let isAddsDone = membersToAdd.length === 0;
      let isDeletesDone = membersToDelete.length === 0;
      if (membersToAdd.length > 0 && folderId) {
        createFolderShares(
          {
            folderId,
            ids: membersToAdd.map(({ id }) => id),
          },
          {
            onSuccess: () => {
              isAddsDone = true;
              if (isDeletesDone) {
                // Resolve if both adding and deleting is done
                resolve();
              }
            },
            onError: (error) => {
              reject(error);
            },
          },
        );
      }
      if (membersToDelete.length > 0 && folderId) {
        deleteFolderShares(
          {
            folderId,
            shareIds: membersToDelete.map(({ id }) => id),
          },
          {
            onSuccess: () => {
              isDeletesDone = true;
              if (isAddsDone) {
                // Resolve if both adding and deleting is done
                resolve();
              }
            },
            onError: (error) => {
              reject(error);
            },
          },
        );
      }
    })
      .then(() => createSuccessToast(folderName))
      .catch(() => createErrorToast(folderName));
  };

  // This callback needs to live here so that it is not canceled when the modal closes
  const handleSubmit = (
    currentFolder: FolderTreeItem | undefined,
    membersToAdd: FolderShare[],
    membersToDelete: FolderShare[],
  ) => {
    if (!currentFolder?.isShared && folderId) {
      submitConvertToSharedFolder(folderId, currentFolder?.name || "");
    } else {
      updateFolderShares(
        membersToAdd,
        membersToDelete,
        currentFolder?.name || "",
      );
    }
  };

  return (
    <ModalDialogRoot isOpen={isOpen} onOpenChange={setIsOpen}>
      {isOpen && <FolderShareModalContent {...props} onSubmit={handleSubmit} />}
    </ModalDialogRoot>
  );
}
